import { VersionRange } from '../../models/core/version-range.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { LocalizationSettings } from '../../models/serializable/localization-settings.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Component } from '../core/component.js';

export class BusinessSettingsComponent extends Component {
  static readonly TYPE_ID: string = '8b154835c5c247f48197fc67b962224a';
  static readonly SCHEMA_VERSION: number = 3;

  constructor(
    public readonly allowCustomBookingSettings: boolean,
    public readonly allowOpenReservations: boolean,
    public readonly settings: LocalizationSettings,
  ) {
    super();
  }

  get $removable(): boolean {
    return false;
  }

  get $typeId(): string {
    return BusinessSettingsComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, BusinessSettingsComponent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): BusinessSettingsComponent {
    return new BusinessSettingsComponent(
        MapUtils.get<boolean>(map, 'allowCustomBookingSettings'),
        MapUtils.get<boolean>(map, 'allowOpenReservations'),
        context.fromData(MapUtils.get<SerializedData>(map, 'settings')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'allowCustomBookingSettings': this.allowCustomBookingSettings,
      'allowOpenReservations': this.allowOpenReservations,
      'settings': context.toData(this.settings),
    };
  }

  clone(): this {
    return new BusinessSettingsComponent(
        this.allowCustomBookingSettings,
        this.allowOpenReservations,
        this.settings.clone(),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.allowCustomBookingSettings === this.allowCustomBookingSettings &&
      other.allowOpenReservations === this.allowOpenReservations &&
      isSameData(other.settings, this.settings);
  }
}

export class BusinessSettingsComponentBuilder extends DataModelBuilder<BusinessSettingsComponent> {
  build(context: SerializationContext, map: SerializedData): BusinessSettingsComponent {
    return BusinessSettingsComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(BusinessSettingsComponent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(BusinessSettingsComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // Version 2 adds the [allowCustomBookingSettings] field.
    if(version < 2) {
      MapUtils.set(map, 'allowCustomBookingSettings', false);
      version = 2;
    }

    // Version 3 adds the [allowOpenReservations] field.
    if(version < 3) {
      MapUtils.set(map, 'allowOpenReservations', false);
      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
